// Client-side capture of HIPAA audit request context.
//
// core.fn_audit_row (v2) reads transaction-local GUCs to populate the
// ip / user_agent / request_id / session_id / route columns on every
// audit_log row. Those GUCs are set per-request via the
// core.rpc_set_request_context RPC.
//
// In the web app this is wired through `supabase()` so every page load
// stamps the request context BEFORE any mutating call (see
// `installRequestContextStamping` below).
//
// Edge functions can call `setRequestContext` directly at the top of
// their handlers using `req.headers` for IP / user-agent.

import { supabase } from '../client';

export interface RequestContextInput {
  /** Originating IP address. From the browser this is usually unknown
   *  and resolved server-side; the RPC tolerates null gracefully. */
  ip?: string | null;
  userAgent?: string | null;
  /** Per-request UUID for cross-system correlation. Frontend generates
   *  one per fetch; edge functions read x-request-id then generate. */
  requestId?: string | null;
  /** Stable per-browser-session UUID (persisted in sessionStorage). */
  sessionId?: string | null;
  /** Application route at the time of the request, e.g.
   *  '/clients/abc' or 'rpc:rpc_org_remove_member'. */
  route?: string | null;
  /** 'user' | 'admin' | 'service_role' | 'system' | 'webhook' | 'cron'
   *  | 'auth_hook' | 'unknown'. Most callers should leave this null
   *  so fn_audit_row defaults to 'user'. */
  actorKind?: string | null;
}

/**
 * Set the request-context GUCs for the next mutation in this PostgREST
 * call. Safe to call before every mutation; it's a cheap RPC.
 */
export async function setRequestContext(input: RequestContextInput): Promise<void> {
  const { error } = await supabase().schema('core').rpc('rpc_set_request_context', {
    p_ip: input.ip ?? undefined,
    p_user_agent: input.userAgent ?? undefined,
    p_request_id: input.requestId ?? undefined,
    p_session_id: input.sessionId ?? undefined,
    p_route: input.route ?? undefined,
    p_actor_kind: input.actorKind ?? undefined,
  });
  if (error) {
    // The audit row is still useful without the context, so we don't
    // hard-fail on a context-stamp failure. We do warn so it shows up
    // in browser devtools and Sentry-style observability tooling.
     
    console.warn('[audit] rpc_set_request_context failed:', error.message);
  }
}

const SESSION_STORAGE_KEY = 'pc.audit.sessionId';

function readBrowserSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    let v = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!v) {
      v = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, v);
    }
    return v;
  } catch {
    return null;
  }
}

function freshRequestId(): string | null {
  if (typeof crypto === 'undefined' || !('randomUUID' in crypto)) return null;
  try {
    return crypto.randomUUID();
  } catch {
    return null;
  }
}

/**
 * Browser-side helper: collect the request context the browser knows
 * about (sessionId from sessionStorage, fresh requestId, current
 * window.location.pathname for the route, navigator.userAgent), and
 * stamp it via the RPC. Call this before each significant mutation,
 * or wrap your service calls with `withRequestContext`.
 *
 * IP is resolved server-side -- PostgREST exposes the originating IP
 * via the request.headers GUC, and edge functions read x-forwarded-for
 * directly. The browser does not reliably know its own egress IP.
 */
export async function stampBrowserRequestContext(routeOverride?: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const route = routeOverride ?? `${window.location.pathname}${window.location.search}`;
  await setRequestContext({
    ip: null,
    userAgent: navigator.userAgent ?? undefined,
    requestId: freshRequestId(),
    sessionId: readBrowserSessionId(),
    route,
  });
}

/**
 * Wraps a mutation-returning async fn so that we stamp the request
 * context BEFORE the mutation runs. Use this around `rpc_admin_*` and
 * other mutating service calls in the operator and admin apps.
 *
 * Example:
 *   await withRequestContext(() => adminRemoveMember(orgId, userId));
 */
export async function withRequestContext<T>(
  fn: () => Promise<T>,
  options?: { route?: string },
): Promise<T> {
  await stampBrowserRequestContext(options?.route);
  return fn();
}
